import { useState } from 'react';
import { useProjects } from '../hooks/useProjects';
import { Folder, Plus, Search, X, Archive, Grid3x3, List } from 'lucide-react';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { CreateProjectModal } from '../components/modals/CreateProjectModal';

interface ProjectsPageProps {
  onProjectSelect?: (projectId: string) => void;
}

export const ProjectsPage = ({ onProjectSelect }: ProjectsPageProps) => {
  const { projects, loading, error, refetch } = useProjects(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Filter projects by search term
  const filteredProjects = projects.filter(project => {
    const searchLower = searchTerm.toLowerCase();
    return (
      project.project_name.toLowerCase().includes(searchLower) ||
      project.project_description?.toLowerCase().includes(searchLower)
    );
  });

  const handleProjectClick = (projectId: string) => {
    if (onProjectSelect) {
      onProjectSelect(projectId);
    } else {
      // Navigate to project detail view
      window.location.href = `/projects/${projectId}`;
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <LoadingSpinner size="lg" />
          <p className="text-th-secondary">Loading your projects...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center space-y-4 max-w-md">
          <div className="text-status-error text-6xl">⚠️</div>
          <h3 className="text-xl font-bold text-th-primary">Failed to Load Projects</h3>
          <p className="text-th-secondary">{error}</p>
          <button onClick={refetch} className="button-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Render empty state
  if (!loading && projects.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center space-y-6 max-w-lg">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-brand/20 rounded-full mb-6">
            <Folder className="w-12 h-12 text-brand" />
          </div>

          <h1 className="text-3xl font-bold text-th-primary">
            Create Your First Project
          </h1>

          <p className="text-lg text-th-secondary">
            Projects help you organize your AI-generated content into collections.
            Create a project to get started!
          </p>

          <button
            onClick={() => setShowCreateModal(true)}
            className="button-primary inline-flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Create Project</span>
          </button>
        </div>

        <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-base/95 backdrop-blur-sm border-b border-subtle">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-th-primary">Projects</h1>
              <p className="text-th-secondary mt-1">
                {projects.length} {projects.length === 1 ? 'project' : 'projects'}
              </p>
            </div>

            <button
              onClick={() => setShowCreateModal(true)}
              className="button-primary inline-flex items-center space-x-2"
            >
              <Plus className="w-5 h-5" />
              <span>New Project</span>
            </button>
          </div>

          {/* Search and View Toggle */}
          <div className="flex items-center space-x-4">
            {/* Search Bar */}
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-th-secondary" />
              </div>
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-10 py-3 bg-elevated border border-subtle rounded-lg text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20 transition-all"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-th-secondary hover:text-th-primary transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center space-x-2 bg-elevated border border-subtle rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded transition-all ${
                  viewMode === 'grid'
                    ? 'bg-brand text-surface'
                    : 'text-th-secondary hover:text-th-primary'
                }`}
              >
                <Grid3x3 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded transition-all ${
                  viewMode === 'list'
                    ? 'bg-brand text-surface'
                    : 'text-th-secondary hover:text-th-primary'
                }`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Projects Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {filteredProjects.length === 0 ? (
          // No search results
          <div className="text-center py-16">
            <Search className="w-16 h-16 text-th-secondary mx-auto mb-4" />
            <h3 className="text-xl font-bold text-th-primary mb-2">No projects found</h3>
            <p className="text-th-secondary">
              No projects match your search for "{searchTerm}". Try different keywords.
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          // Grid View
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProjects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onClick={() => handleProjectClick(project.id)}
              />
            ))}
          </div>
        ) : (
          // List View
          <div className="space-y-4">
            {filteredProjects.map((project) => (
              <ProjectListItem
                key={project.id}
                project={project}
                onClick={() => handleProjectClick(project.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Background Effects */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      </div>

      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={(projectId) => {
          console.log('Project created:', projectId);
          refetch();
        }}
      />
    </div>
  );
};

// Project Card Component (Grid View)
interface ProjectCardProps {
  project: any;
  onClick: () => void;
}

const ProjectCard = ({ project, onClick }: ProjectCardProps) => {
  return (
    <div
      onClick={onClick}
      className="group bg-elevated rounded-lg overflow-hidden cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:shadow-lg border border-subtle hover:border-brand/50"
    >
      {/* Thumbnail */}
      <div
        className="w-full h-48 bg-brand/20 relative"
        style={{
          backgroundColor: project.project_color || '#6b6b70',
          opacity: 0.3
        }}
      >
        {project.thumbnail_url ? (
          <img
            src={project.thumbnail_url}
            alt={project.project_name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Folder className="w-16 h-16 text-white opacity-50" />
          </div>
        )}

        {/* Asset Count Badge */}
        <div className="absolute top-3 right-3 bg-black/70 backdrop-blur-sm px-3 py-1 rounded-full text-white text-sm font-medium">
          {project.asset_count} {project.asset_count === 1 ? 'asset' : 'assets'}
        </div>
      </div>

      {/* Project Info */}
      <div className="p-4">
        <h3 className="text-lg font-semibold text-th-primary mb-1 truncate">
          {project.project_name}
        </h3>

        {project.project_description && (
          <p className="text-sm text-th-secondary line-clamp-2 mb-3">
            {project.project_description}
          </p>
        )}

        <div className="flex items-center justify-between text-xs text-th-secondary">
          <span>Updated {new Date(project.updated_at).toLocaleDateString()}</span>
          {project.is_archived && (
            <span className="flex items-center space-x-1 text-status-warning">
              <Archive className="w-3 h-3" />
              <span>Archived</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Project List Item Component (List View)
const ProjectListItem = ({ project, onClick }: ProjectCardProps) => {
  return (
    <div
      onClick={onClick}
      className="bg-elevated rounded-lg p-4 cursor-pointer transition-all duration-200 hover:bg-overlay border border-subtle hover:border-brand/50 flex items-center space-x-4"
    >
      {/* Thumbnail */}
      <div
        className="w-20 h-20 rounded-lg bg-brand/20 flex-shrink-0 relative"
        style={{
          backgroundColor: project.project_color || '#6b6b70',
          opacity: 0.3
        }}
      >
        {project.thumbnail_url ? (
          <img
            src={project.thumbnail_url}
            alt={project.project_name}
            className="w-full h-full object-cover rounded-lg"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Folder className="w-8 h-8 text-white opacity-50" />
          </div>
        )}
      </div>

      {/* Project Info */}
      <div className="flex-1 min-w-0">
        <h3 className="text-lg font-semibold text-th-primary truncate">
          {project.project_name}
        </h3>
        {project.project_description && (
          <p className="text-sm text-th-secondary truncate">
            {project.project_description}
          </p>
        )}
      </div>

      {/* Stats */}
      <div className="flex items-center space-x-6 text-sm">
        <div className="text-center">
          <div className="text-th-primary font-semibold">{project.asset_count}</div>
          <div className="text-th-secondary text-xs">Assets</div>
        </div>
        <div className="text-center">
          <div className="text-th-secondary text-xs">Updated</div>
          <div className="text-th-primary text-xs">
            {new Date(project.updated_at).toLocaleDateString()}
          </div>
        </div>
        {project.is_archived && (
          <div className="flex items-center space-x-1 text-status-warning">
            <Archive className="w-4 h-4" />
          </div>
        )}
      </div>
    </div>
  );
};
